import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Download,
  Search,
  Trash2,
  UserCog,
  UserX,
  UserCheck,
  X,
} from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Modal } from "@/design-system/components/Modal";
import { Button } from "@/design-system/components/Button";
import { SemanticStatusBadge } from "@/design-system/components/SemanticStatusBadge";
import { Section } from "@/admin/_primitives";

const ROLES = ["user", "admin"];
const TEAM_ROLES = ["", "owner", "admin", "manager", "support", "dispute_specialist", "funding_specialist"];
const ACCESS_LEVELS = ["free", "member", "premium", "admin"];
const PAGE_SIZES = [25, 50, 100, 200];

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    team_role: "",
    access_level: "",
    is_suspended: "",
    profile_completed: "",
  });
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [openUserId, setOpenUserId] = useState(null);

  // Debounce query.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Any change to search / filter / page size resets to first page.
  useEffect(() => { setOffset(0); }, [debouncedQ, filters, limit]);

  const params = useMemo(() => {
    const p = {};
    if (debouncedQ.trim()) p.q = debouncedQ.trim();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null) p[k] = v;
    });
    p.limit = limit;
    p.offset = offset;
    return p;
  }, [debouncedQ, filters, limit, offset]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/users-enriched", { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/users/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_export_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, "")}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("CSV export failed.");
    }
  };

  return (
    <AdminLayout
      title="Users"
      subtitle={`${total} total · ${items.length} on this page`}
      actions={
        <button
          type="button"
          onClick={downloadCsv}
          data-testid="admin-users-export-csv"
          className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-[12px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface)]"
        >
          <Download className="w-3 h-3" /> Export CSV
        </button>
      }
    >
      {/* Search + filters */}
      <AdminPageHelp
        title="Users"
        purpose="Browse and manage every account in the platform — customer users AND fellow admins/teammates."
        configures="Search by email/name/phone, filter by role/access level/suspension state, then click any row to edit their role, team role, or access level, or suspend/reactivate the account."
        impact="Role and access-level changes take effect immediately. A suspended user is blocked from logging in and their active session is invalidated. Deleting a user is a hard delete (also removes their credit reports and dispute letters)."
        safety="Role / access-level / team-role changes are recorded in the admin audit log and can be reverted here on the same page. Deletions are NOT reversible — do not delete an account you may need later."
      />
      <div className="grid md:grid-cols-[1fr_auto] gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ds-color-ink-muted)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, or phone…"
            data-testid="admin-users-search"
            className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] pl-9 pr-3 py-1.5 text-[13px] text-[var(--ds-color-ink)] placeholder:text-[var(--ds-color-ink-muted)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
          />
        </div>
        <button
          type="button"
          data-testid="admin-users-refresh"
          onClick={load}
          className="rounded-[var(--ds-radius-md)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-[12px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <FilterSelect testId="filter-role" label="Role" value={filters.role} onChange={(v) => setFilters((f) => ({ ...f, role: v }))} options={["", ...ROLES]} />
        <FilterSelect testId="filter-team-role" label="Team role" value={filters.team_role} onChange={(v) => setFilters((f) => ({ ...f, team_role: v }))} options={TEAM_ROLES} />
        <FilterSelect testId="filter-access" label="Access" value={filters.access_level} onChange={(v) => setFilters((f) => ({ ...f, access_level: v }))} options={["", ...ACCESS_LEVELS]} />
        <FilterSelect
          testId="filter-suspended"
          label="Suspended"
          value={filters.is_suspended}
          onChange={(v) => setFilters((f) => ({ ...f, is_suspended: v }))}
          options={[
            { value: "", label: "any" },
            { value: "true", label: "true" },
            { value: "false", label: "false" },
          ]}
        />
        <FilterSelect
          testId="filter-profile"
          label="Profile"
          value={filters.profile_completed}
          onChange={(v) => setFilters((f) => ({ ...f, profile_completed: v }))}
          options={[
            { value: "", label: "any" },
            { value: "true", label: "complete" },
            { value: "false", label: "incomplete" },
          ]}
        />
      </div>

      {error && (
        <div data-testid="admin-users-error" className="mb-3 text-sm" style={{ color: "var(--ds-color-danger)" }}>{error}</div>
      )}

      <div
        data-testid="admin-users-table"
        className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] overflow-hidden"
        style={{ background: "var(--ds-color-surface)" }}
      >
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.16em] text-[var(--ds-color-ink-muted)] border-b border-[var(--ds-color-admin-border)]">
            <tr>
              <Th>Email</Th>
              <Th>Name</Th>
              <Th>Created</Th>
              <Th>Role</Th>
              <Th>Access</Th>
              <Th>Readiness</Th>
              <Th>Reports</Th>
              <Th>Suspended</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan="9" className="text-center text-[var(--ds-color-ink-muted)] py-8 text-sm">No users match.</td></tr>
            )}
            {items.map((u) => (
              <tr
                key={u.id}
                data-testid={`admin-user-row-${u.id}`}
                className="border-b border-[var(--ds-color-admin-border)] hover:bg-[var(--ds-color-accent-soft)] cursor-pointer"
                onClick={() => setOpenUserId(u.id)}
              >
                <Td className="font-medium truncate max-w-[220px]">{u.email}</Td>
                <Td className="truncate max-w-[180px]">{u.first_name || ""} {u.last_name || ""}</Td>
                <Td className="text-[var(--ds-color-ink-muted)] whitespace-nowrap text-xs">{u.created_at?.slice(0, 10)}</Td>
                <Td><Badge color={u.role === "admin" ? "var(--ds-color-accent)" : "var(--ds-color-ink-muted)"}>{u.role}</Badge></Td>
                <Td><Badge>{u.access_level}</Badge></Td>
                <Td className="tabular-nums">
                  {u.latest_readiness_score != null ? (
                    <span>{u.latest_readiness_score}<span className="text-[var(--ds-color-ink-muted)] text-[10px] ml-1">{u.latest_readiness_tier?.replace("_", " ")}</span></span>
                  ) : "—"}
                </Td>
                <Td className="text-center">{u.latest_credit_report_id ? <Check className="w-3.5 h-3.5 inline text-emerald-400" aria-label="Has credit report" /> : "—"}</Td>
                <Td>{u.is_suspended ? <Badge color="var(--ds-color-danger)">suspended</Badge> : <span className="text-[var(--ds-color-ink-muted)]">—</span>}</Td>
                <Td>
                  <button
                    type="button"
                    data-testid={`admin-user-row-${u.id}-open`}
                    onClick={(e) => { e.stopPropagation(); setOpenUserId(u.id); }}
                    className="text-[var(--ds-color-accent)] text-xs hover:underline"
                  >
                    Manage
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div
        data-testid="admin-users-pagination"
        className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-xs"
        style={{ borderColor: "var(--ds-color-admin-border)", background: "var(--ds-color-admin-surface-muted)", color: "var(--ds-color-ink-muted)" }}
      >
        <div className="flex items-center gap-2">
          <span>Page size</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            data-testid="admin-users-page-size"
            aria-label="Rows per page"
            className="rounded border px-2 py-1"
            style={{ borderColor: "var(--ds-color-admin-border)", background: "var(--ds-color-surface)", color: "var(--ds-color-ink)" }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="admin-users-page-indicator">
            Page {currentPage} of {totalPages} · {total} total
          </span>
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0 || loading}
            data-testid="admin-users-prev"
            className="rounded border px-2 py-1 disabled:opacity-40"
            style={{ borderColor: "var(--ds-color-admin-border)", color: "var(--ds-color-ink)" }}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total || loading}
            data-testid="admin-users-next"
            className="rounded border px-2 py-1 disabled:opacity-40"
            style={{ borderColor: "var(--ds-color-admin-border)", color: "var(--ds-color-ink)" }}
          >
            Next
          </button>
        </div>
      </div>

      {openUserId && (
        <UserDrawer
          userId={openUserId}
          isMe={openUserId === me?.id}
          onClose={() => setOpenUserId(null)}
          onChanged={() => { load(); }}
        />
      )}
    </AdminLayout>
  );
}

function Th({ children }) {
  return <th className="text-left px-3 py-2.5 font-normal">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}
function Badge({ children, color = "var(--ds-color-ink-muted)" }) {
  return (
    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.1em]"
      style={{ background: `${color}1f`, color, border: `1px solid ${color}44` }}>
      {children}
    </span>
  );
}
function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="text-xs text-[var(--ds-color-ink-muted)]">
      <span className="block mb-1 uppercase tracking-[0.12em] text-[10px]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2 py-1 text-[12px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
      >
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.value;
          const lbl = typeof o === "string" ? (o === "" ? "any" : o.replace("_", " ")) : o.label;
          return <option key={v} value={v}>{lbl}</option>;
        })}
      </select>
    </label>
  );
}

// ---- User drawer ----------------------------------------------------------


function UserDrawer({ userId, isMe, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/users/${userId}/detail`).then((r) => {
      if (cancelled) return;
      setDetail(r.data);
      setForm({
        role: r.data.user.role,
        team_role: r.data.user.team_role || "",
        access_level: r.data.user.access_level,
        admin_notes: r.data.user.admin_notes || "",
      });
    }).catch((e) => setError(e?.response?.data?.detail || "Failed to load detail.")).finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [userId]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        role: form.role,
        team_role: form.team_role || null,
        access_level: form.access_level,
        admin_notes: form.admin_notes,
      };
      await api.patch(`/admin/users/${userId}`, body);
      setEditing(false);
      onChanged();
      // Refresh detail.
      const r = await api.get(`/admin/users/${userId}/detail`);
      setDetail(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save.");
    } finally {
      setBusy(false);
    }
  };

  const toggleSuspend = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, { is_suspended: !detail.user.is_suspended });
      onChanged();
      const r = await api.get(`/admin/users/${userId}/detail`);
      setDetail(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to toggle suspend.");
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async () => {
    if (deleteConfirm !== "DELETE") return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${userId}`);
      onChanged();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete user.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} />
      <div
        data-testid="admin-user-drawer"
        className="absolute right-0 top-0 bottom-0 w-full max-w-2xl overflow-y-auto"
        style={{ background: "var(--ds-color-admin-surface)", borderLeft: "1px solid var(--ds-color-admin-border)" }}
      >
        <header className="sticky top-0 flex items-center justify-between border-b border-[var(--ds-color-admin-border)] px-5 py-4 z-10" style={{ background: "var(--ds-color-admin-surface)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="admin-user-drawer-back"
              className="inline-flex items-center gap-1 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">User detail</div>
              <div className="font-display text-base text-[var(--ds-color-ink)] truncate">{detail?.user?.email || "…"}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="admin-user-drawer-close" className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-5 space-y-5">
          {loading && <div className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div>}
          {error && <div className="text-sm" style={{ color: "var(--ds-color-danger)" }}>{error}</div>}
          {detail && (
            <>
              {/* Profile summary */}
              <Section title="Profile">
                <Grid>
                  <Field label="Name" value={`${detail.user.first_name || ""} ${detail.user.last_name || ""}`.trim() || "—"} />
                  <Field label="Phone" value={detail.user.phone || "—"} />
                  <Field label="State" value={detail.user.state || "—"} />
                  <Field label="Credit score" value={detail.user.estimated_credit_score || "—"} />
                  <Field label="Business owner" value={detail.user.is_business_owner ? "Yes" : "No"} />
                  <Field label="Monthly revenue" value={detail.user.monthly_revenue != null ? `$${detail.user.monthly_revenue}` : "—"} />
                  <Field label="Referrals" value={detail.referral_signups} />
                  <Field label="Joined" value={detail.user.created_at?.slice(0, 10)} />
                </Grid>
              </Section>

              {/* Roles */}
              <Section
                title="Roles & access"
                actions={
                  editing ? (
                    <div className="flex gap-2">
                      <button data-testid="admin-user-edit-cancel" onClick={() => setEditing(false)} disabled={busy} className="text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]">Cancel</button>
                      <button data-testid="admin-user-edit-save" onClick={save} disabled={busy} className="text-xs rounded-md px-3 py-1.5" style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}>
                        {busy ? "Saving…" : "Save"}
                      </button>
                    </div>
                  ) : (
                    <button data-testid="admin-user-edit-start" onClick={() => setEditing(true)} className="text-xs text-[var(--ds-color-accent)] hover:underline inline-flex items-center gap-1">
                      <UserCog className="w-3 h-3" /> Edit
                    </button>
                  )
                }
              >
                {editing ? (
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <FormSelect label="Role" testId="edit-role" value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))} options={ROLES} />
                    <FormSelect label="Team role" testId="edit-team-role" value={form.team_role} onChange={(v) => setForm((f) => ({ ...f, team_role: v }))} options={TEAM_ROLES} />
                    <FormSelect label="Access level" testId="edit-access" value={form.access_level} onChange={(v) => setForm((f) => ({ ...f, access_level: v }))} options={ACCESS_LEVELS} />
                    <label className="text-xs text-[var(--ds-color-ink-muted)] sm:col-span-2">
                      <span className="block mb-1 uppercase tracking-[0.12em] text-[10px]">Admin notes</span>
                      <textarea
                        data-testid="edit-admin-notes"
                        value={form.admin_notes}
                        onChange={(e) => setForm((f) => ({ ...f, admin_notes: e.target.value }))}
                        rows={3}
                        className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2 py-1 text-[12px] text-[var(--ds-color-ink)]"
                      />
                    </label>
                  </div>
                ) : (
                  <Grid>
                    <Field label="Role" value={detail.user.role} />
                    <Field label="Team role" value={detail.user.team_role || "—"} />
                    <Field label="Access" value={detail.user.access_level} />
                    <Field label="Suspended" value={detail.user.is_suspended ? "yes" : "no"} />
                    <Field label="Admin notes" value={detail.user.admin_notes || "—"} full />
                  </Grid>
                )}
              </Section>

              {/* V1.1 UTM-saga closure (Option 2) — Acquisition rendered from
                  analytics events (`signup_success`) instead of the user-doc
                  `acquisition_*` fields, which were intermittently null. See
                  /app/memory/v11_invite_performance_progress.md. */}
              <Section title="Acquisition">
                {detail.acquisition_from_events ? (
                  <div data-testid="admin-user-acquisition" className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      <Field label="Cohort" value={detail.acquisition_from_events.cohort || "—"} />
                      <Field label="Early access code used" value={detail.acquisition_from_events.beta_code || "—"} />
                      <Field label="utm_source" value={detail.acquisition_from_events.utm_source || "—"} />
                      <Field label="utm_medium" value={detail.acquisition_from_events.utm_medium || "—"} />
                      <Field label="utm_campaign" value={detail.acquisition_from_events.utm_campaign || "—"} />
                      <Field label="utm_content" value={detail.acquisition_from_events.utm_content || "—"} />
                      <Field
                        label="Landing URL"
                        value={detail.acquisition_from_events.landing_url || "—"}
                        full
                      />
                      <Field
                        label="Acquired at"
                        value={
                          detail.acquisition_from_events.acquired_at
                            ? detail.acquisition_from_events.acquired_at.slice(0, 16).replace("T", " ")
                            : "—"
                        }
                      />
                    </div>
                    <p
                      data-testid="admin-user-acquisition-source-tag"
                      className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]"
                    >
                      Source: analytics events stream
                    </p>
                  </div>
                ) : (
                  <div data-testid="admin-user-acquisition-empty" className="text-sm text-[var(--ds-color-ink-muted)]">
                    No acquisition event recorded.
                  </div>
                )}
              </Section>

              {/* History */}
              <Section title={`Readiness history (${detail.readiness_history.length})`}>
                {detail.readiness_history.length === 0 ? (
                  <Empty>No assessments yet.</Empty>
                ) : (
                  <ul className="text-sm divide-y divide-[var(--ds-color-admin-border)]">
                    {detail.readiness_history.slice(0, 6).map((r) => (
                      <li key={r.id} className="flex items-center justify-between py-1.5 text-xs">
                        <span className="text-[var(--ds-color-ink-muted)]">{r.created_at?.slice(0, 10)}</span>
                        <span className="tabular-nums">{r.total_score}</span>
                        <span className="text-[var(--ds-color-ink-muted)]">{r.tier?.replace("_", " ")}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={`Credit reports (${detail.credit_reports.length})`}>
                {detail.credit_reports.length === 0 ? (
                  <Empty>No reports uploaded.</Empty>
                ) : (
                  <ul className="text-sm divide-y divide-[var(--ds-color-admin-border)]" data-testid="admin-user-credit-reports-list">
                    {detail.credit_reports.slice(0, 6).map((r) => (
                      <CreditReportRow
                        key={r.id}
                        report={r}
                        onDeleted={async () => {
                          const fresh = await api.get(`/admin/users/${userId}/detail`);
                          setDetail(fresh.data);
                          onChanged();
                        }}
                      />
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={`Dispute letters (${detail.dispute_letters.length})`}>
                {detail.dispute_letters.length === 0 ? (
                  <Empty>None.</Empty>
                ) : (
                  <ul className="text-sm divide-y divide-[var(--ds-color-admin-border)]">
                    {detail.dispute_letters.slice(0, 6).map((l) => (
                      <li key={l.id} className="flex items-center justify-between py-1.5 text-xs">
                        <span className="text-[var(--ds-color-ink-muted)]">{l.letter_type}</span>
                        <span className="text-[var(--ds-color-ink-muted)]">{l.bureau}</span>
                        <span className="text-[var(--ds-color-ink-muted)]">{l.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={`Partner matches (${detail.partner_matches.length})`}>
                {detail.partner_matches.length === 0 ? (
                  <Empty>None.</Empty>
                ) : (
                  <ul className="text-sm divide-y divide-[var(--ds-color-admin-border)]">
                    {detail.partner_matches.slice(0, 6).map((p) => (
                      <li key={p.id} className="flex items-center justify-between py-1.5 text-xs">
                        <span className="text-[var(--ds-color-ink-muted)] truncate max-w-[200px]">{p.partner_name}</span>
                        <span className="text-[var(--ds-color-ink-muted)]">{p.match_tier}</span>
                        <span className="tabular-nums">{p.score}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Danger zone */}
              <Section title="Danger zone">
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={toggleSuspend}
                    disabled={busy || isMe}
                    data-testid="admin-user-suspend-toggle"
                    className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs disabled:opacity-50"
                    style={{
                      borderColor: detail.user.is_suspended ? "var(--ds-color-success)55" : "var(--ds-color-warning)55",
                      color: detail.user.is_suspended ? "var(--ds-color-success)" : "var(--ds-color-warning)",
                    }}
                  >
                    {detail.user.is_suspended ? <><UserCheck className="w-3 h-3" /> Reactivate</> : <><UserX className="w-3 h-3" /> Suspend</>}
                  </button>
                  {isMe && <p className="text-[11px] text-[var(--ds-color-ink-muted)]">You cannot suspend or delete your own account.</p>}
                  <div className="border-t border-[var(--ds-color-admin-border)] pt-3">
                    <div className="text-xs text-[var(--ds-color-ink-muted)] mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" style={{ color: "var(--ds-color-danger)" }} /> Type <code>DELETE</code> to enable user deletion (cascades all data).
                    </div>
                    <div className="flex gap-2">
                      <input
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        placeholder="DELETE"
                        data-testid="admin-user-delete-confirm-input"
                        className="flex-1 rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2 py-1 text-[12px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-danger)]"
                      />
                      <button
                        type="button"
                        onClick={deleteUser}
                        disabled={isMe || busy || deleteConfirm !== "DELETE"}
                        data-testid="admin-user-delete-confirm"
                        className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs disabled:opacity-30"
                        style={{ borderColor: "var(--ds-color-danger)", color: "var(--ds-color-danger)" }}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-xs">{children}</div>;
}
function Field({ label, value, full }) {
  return (
    <div className={full ? "col-span-2 sm:col-span-3" : ""}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">{label}</div>
      <div className="text-[var(--ds-color-ink)] truncate">{value || "—"}</div>
    </div>
  );
}
function Empty({ children }) {
  return <div className="text-[11px] text-[var(--ds-color-ink-muted)] py-1">{children}</div>;
}
function FormSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="text-xs text-[var(--ds-color-ink-muted)]">
      <span className="block mb-1 uppercase tracking-[0.12em] text-[10px]">{label}</span>
      <select
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-2 py-1 text-[12px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o === "" ? "—" : o.replace("_", " ")}</option>
        ))}
      </select>
    </label>
  );
}

// ---- Credit report row (Sprint 1 Compliance) -------------------------------
//
// Renders a single credit-report row inside the user drawer with:
//   * filename / bureau / score / extraction_status
//   * Consent-present or consent-missing chip powered by
//     SemanticStatusBadge (uses the same glyph-to-icon translator the rest
//     of the app uses so the DOM never emits a raw emoji character)
//   * Delete report button - confirmation modal with focus trap +
//     ESC-close (design-system Modal primitive; a11y contract per Phase 6.2)
function formatConsentIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toISOString().slice(0, 10);
}

function CreditReportRow({ report, onDeleted }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const consentIso = report.upload_consent_acknowledged_at;
  const consentLabel = consentIso
    ? `Consent ${formatConsentIso(consentIso)}`
    : "Consent missing";

  const handleDelete = async () => {
    setBusy(true);
    setError("");
    try {
      await api.delete(`/admin/credit-reports/${report.id}`);
      setConfirmOpen(false);
      await onDeleted();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-xs"
      data-testid={`admin-user-credit-report-row-${report.id}`}
    >
      <span className="text-[var(--ds-color-ink-muted)] truncate max-w-[200px]">
        {report.original_filename || "—"}
      </span>
      <span className="text-[var(--ds-color-ink-muted)]">{report.bureau || "—"}</span>
      <span className="tabular-nums">{report.score ?? "—"}</span>
      <span className="text-[var(--ds-color-ink-muted)]">{report.extraction_status || "—"}</span>
      <SemanticStatusBadge
        emoji={consentIso ? "✅" : "⚠️"}
        label={consentLabel}
        data-testid={
          consentIso
            ? `admin-user-consent-ok-${report.id}`
            : `admin-user-consent-missing-${report.id}`
        }
      />
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        data-testid={`admin-user-credit-report-delete-${report.id}`}
        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] uppercase tracking-wide"
        style={{ borderColor: "var(--ds-color-danger)", color: "var(--ds-color-danger)" }}
      >
        <Trash2 className="w-3 h-3" aria-hidden="true" />
        Delete report
      </button>
      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this credit report?"
        description="This removes the extracted analysis and the original file from storage. The user is not deleted."
        data-testid={`admin-user-credit-report-delete-modal-${report.id}`}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              data-testid={`admin-user-credit-report-delete-cancel-${report.id}`}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              loading={busy}
              disabled={busy}
              data-testid={`admin-user-credit-report-delete-confirm-${report.id}`}
            >
              {busy ? "Deleting…" : "Delete report data"}
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-[13px]">
          <div>
            <span className="text-[var(--ds-color-ink-muted)]">Filename:</span>{" "}
            <span data-testid={`admin-user-credit-report-delete-filename-${report.id}`}>
              {report.original_filename || "—"}
            </span>
          </div>
          <div>
            <span className="text-[var(--ds-color-ink-muted)]">Bureau:</span>{" "}
            <span>{report.bureau || "—"}</span>
          </div>
          <div>
            <span className="text-[var(--ds-color-ink-muted)]">Uploaded:</span>{" "}
            <span>{report.created_at?.slice(0, 10) || "—"}</span>
          </div>
          {error ? (
            <div
              role="alert"
              data-testid={`admin-user-credit-report-delete-error-${report.id}`}
              className="text-[12px] text-[var(--ds-color-danger)]"
            >
              {error}
            </div>
          ) : null}
        </div>
      </Modal>
    </li>
  );
}

